import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { isSuperAdmin } from '../lib/auth';
import { copy } from '../lib/copy';
import { useToast } from '../context/ToastContext';
import { ErrorState, LoadingState, PageHeader } from '../components/ui';
import {
  StudioConfigV2Editor,
  type StudioConfigV2Draft,
} from '../components/StudioConfigV2Editor';
import { StudioPricingTabs } from '../components/StudioPricingTabs';

export function ModelStudioEditorPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const canEdit = isSuperAdmin();
  const [draft, setDraft] = useState<StudioConfigV2Draft | null>(null);
  const [manualCapabilitiesJson, setManualCapabilitiesJson] = useState('');

  const { data: v2Data, isLoading, isError, refetch } = useQuery({
    queryKey: ['model-studio-config-v2', id],
    queryFn: () => adminApi.getModelStudioConfigV2(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    const source = v2Data?.draft?.config ?? v2Data?.published?.config;
    if (source) {
      setDraft(structuredClone(source) as StudioConfigV2Draft);
    }
  }, [v2Data]);

  const saveDraftMutation = useMutation({
    mutationFn: (payload: StudioConfigV2Draft) =>
      adminApi.saveModelStudioConfigDraft(id, payload as Record<string, unknown>),
    onSuccess: () => {
      void refetch();
      queryClient.invalidateQueries({ queryKey: ['models'] });
      pushToast('Draft v2 сохранён', 'success');
    },
    onError: () => pushToast(copy.errors.updateStudioModel, 'error'),
  });

  const publishV2Mutation = useMutation({
    mutationFn: () => adminApi.publishModelStudioConfigV2(id),
    onSuccess: () => {
      void refetch();
      pushToast('Config v2 опубликован', 'success');
    },
    onError: () => pushToast(copy.errors.updateStudioModel, 'error'),
  });

  const validateV2Mutation = useMutation({
    mutationFn: () => adminApi.validateModelStudioConfigV2(id, draft ?? undefined),
    onSuccess: (result) => {
      pushToast(
        result.valid ? 'Валидация v2 пройдена' : `Ошибки: ${(result.errors ?? []).join(', ')}`,
        result.valid ? 'success' : 'error',
      );
    },
  });

  const syncCapsMutation = useMutation({
    mutationFn: () => adminApi.syncModelCapabilities(id),
    onSuccess: () => {
      void refetch();
      pushToast('Capabilities синхронизированы', 'success');
    },
    onError: () => pushToast('Ошибка синхронизации capabilities', 'error'),
  });

  const importCapsMutation = useMutation({
    mutationFn: () => adminApi.importModelStudioCapabilities(id),
    onSuccess: (result) => {
      setDraft(structuredClone(result.config) as StudioConfigV2Draft);
      void refetch();
      pushToast('Изменения provider capabilities импортированы в draft', 'success');
    },
    onError: () => pushToast('Не удалось импортировать capabilities', 'error'),
  });

  const rollbackV2Mutation = useMutation({
    mutationFn: (version: number) => adminApi.rollbackModelStudioConfigV2(id, version),
    onSuccess: () => {
      void refetch();
      pushToast('Config v2 откачен', 'success');
    },
    onError: () => pushToast('Не удалось откатить config v2', 'error'),
  });

  const manualCapsMutation = useMutation({
    mutationFn: () =>
      adminApi.saveModelManualCapabilities(
        id,
        JSON.parse(manualCapabilitiesJson) as Record<string, unknown>,
      ),
    onSuccess: () => {
      void refetch();
      pushToast('Manual capabilities сохранены как новый snapshot', 'success');
    },
    onError: () => pushToast('Не удалось сохранить manual capabilities', 'error'),
  });

  const syncPricingMutation = useMutation({
    mutationFn: () => adminApi.syncModelStudioPricing(id),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['model-studio-pricing', id] });
      const draftCreated = Boolean(
        result && typeof result === 'object' && 'draft' in result && result.draft,
      );
      pushToast(
        draftCreated
          ? 'Provider price синхронизирован, создан draft sell price'
          : 'Provider price синхронизирован',
        'success',
      );
    },
    onError: () => pushToast('Ошибка синхронизации provider price', 'error'),
  });

  const createSellDraftMutation = useMutation({
    mutationFn: () => adminApi.createModelStudioSellPriceDraft(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['model-studio-pricing', id] });
      pushToast('Draft sell price создан', 'success');
    },
    onError: () => pushToast('Не удалось создать draft sell price', 'error'),
  });

  const publishSellDraftMutation = useMutation({
    mutationFn: () => adminApi.publishModelStudioSellPriceDraft(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['model-studio-pricing', id] });
      pushToast('Sell price опубликован', 'success');
    },
    onError: () => pushToast('Ошибка публикации sell price', 'error'),
  });

  const rollbackSellMutation = useMutation({
    mutationFn: (version: number) => adminApi.rollbackModelStudioSellPrice(id, version),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['model-studio-pricing', id] });
      pushToast('Sell price откатан', 'success');
    },
    onError: () => pushToast('Ошибка rollback sell price', 'error'),
  });

  const { data: pricingDetail, isLoading: pricingLoading } = useQuery({
    queryKey: ['model-studio-pricing', id],
    queryFn: () => adminApi.getModelStudioPricing(id),
    enabled: Boolean(id),
  });

  const { data: v2Preview } = useQuery({
    queryKey: ['model-studio-config-v2-preview-matrix', id],
    queryFn: () => adminApi.previewModelStudioConfigV2Matrix(id),
    enabled: Boolean(id),
  });

  const { data: capabilityHistory } = useQuery({
    queryKey: ['model-capability-history', id],
    queryFn: () => adminApi.getModelCapabilityHistory(id),
    enabled: Boolean(id),
  });

  const previewRows = useMemo(() => {
    if (!v2Preview) return [];
    return Object.entries(v2Preview).map(([planId, preview]) => {
      if (!preview) {
        return {
          planId,
          status: 'unavailable',
          allowed: false,
          minPlanId: '—',
          credits: '—',
        };
      }
      const planAccess = preview.planAccess as
        | { allowed?: boolean; minPlanId?: string }
        | undefined;
      const pricePreview = preview.pricePreview as { credits?: number } | undefined;
      return {
        planId,
        status: String(preview.status ?? 'unknown'),
        allowed: Boolean(planAccess?.allowed),
        minPlanId: planAccess?.minPlanId ?? '—',
        credits: pricePreview?.credits != null ? String(pricePreview.credits) : '—',
      };
    });
  }, [v2Preview]);

  const catalogTitle = useMemo(() => {
    const catalog = draft?.catalog as { displayName?: string } | undefined;
    return catalog?.displayName ?? v2Data?.model_id ?? id;
  }, [draft, v2Data, id]);

  if (isLoading) return <LoadingState label={copy.common.loading} />;
  if (isError) {
    return <ErrorState message={copy.errors.loadStudioModel} onRetry={() => refetch()} />;
  }
  if (!v2Data || !draft) {
    return <ErrorState message={copy.models.studioEditorNotCatalog} onRetry={() => refetch()} />;
  }

  return (
    <div>
      <div className="mb-4">
        <Link to="/studio/models" className="text-sm text-ops-muted hover:text-ops-text">
          ← {copy.models.studioEditorBack}
        </Link>
      </div>
      <PageHeader
        title={`${copy.models.studioEditorTitle}: ${catalogTitle}`}
        description="Редактор config v2: constraints, pricing, publish"
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm text-ops-muted">
        <span>Model: {v2Data.model_id}</span>
        <span>Published: {v2Data.published?.version ?? '—'}</span>
        <span>Draft: {v2Data.draft?.version ?? '—'}</span>
        {v2Data.validation ? (
          <span className={v2Data.validation.valid ? 'text-green-600' : 'text-red-500'}>
            {v2Data.validation.valid ? 'Valid' : 'Invalid'}
          </span>
        ) : null}
        <span className={v2Data.readiness.ready ? 'text-green-600' : 'text-amber-500'}>
          {v2Data.readiness.ready ? 'Production ready' : 'Configuration required'}
        </span>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <StudioConfigV2Editor
          config={draft}
          disabled={!canEdit}
          onChange={setDraft}
        />

        <div className="space-y-4">
          <StudioPricingTabs
            pricing={pricingDetail}
            isLoading={pricingLoading}
            canSync={canEdit}
            isSyncing={syncPricingMutation.isPending}
            onSync={() => syncPricingMutation.mutate()}
            canEditSell={canEdit}
            isCreatingDraft={createSellDraftMutation.isPending}
            isPublishing={publishSellDraftMutation.isPending}
            isRollingBack={rollbackSellMutation.isPending}
            onCreateDraft={() => createSellDraftMutation.mutate()}
            onPublishDraft={() => publishSellDraftMutation.mutate()}
            onRollback={(version) => rollbackSellMutation.mutate(version)}
          />

          <section className="ops-card space-y-3 p-4">
            <h2 className="text-base font-semibold">Preview по тарифам</h2>
            {previewRows.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-ops-border">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-ops-surface text-ops-muted">
                    <tr>
                      <th className="px-3 py-2 font-medium">План</th>
                      <th className="px-3 py-2 font-medium">Статус</th>
                      <th className="px-3 py-2 font-medium">Доступ</th>
                      <th className="px-3 py-2 font-medium">Min plan</th>
                      <th className="px-3 py-2 font-medium">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => (
                      <tr key={row.planId} className="border-t border-ops-border">
                        <td className="px-3 py-2">{row.planId}</td>
                        <td className="px-3 py-2">{row.status}</td>
                        <td className="px-3 py-2">{row.allowed ? 'yes' : 'no'}</td>
                        <td className="px-3 py-2">{row.minPlanId}</td>
                        <td className="px-3 py-2">{row.credits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <pre className="max-h-96 overflow-auto rounded-lg bg-ops-surface p-3 text-xs text-ops-muted">
              {JSON.stringify(v2Preview ?? {}, null, 2)}
            </pre>
          </section>

          {canEdit ? (
            <section className="ops-card space-y-3 p-4">
              <h2 className="text-base font-semibold">Manual capability snapshot</h2>
              <p className="text-xs text-ops-muted">
                Для OpenRouter: sourceUrl, expiresAt, verificationEvidence с
                testRequestPayload/resultSummary и normalizedCapabilities.
              </p>
              <textarea
                className="min-h-40 w-full rounded-lg border border-ops-border bg-ops-surface p-3 font-mono text-xs"
                value={manualCapabilitiesJson}
                placeholder='{"sourceUrl":"https://...","verificationEvidence":{},"normalizedCapabilities":{"schemaVersion":2,"source":"manual_verified"}}'
                onChange={(event) => setManualCapabilitiesJson(event.target.value)}
              />
              <button
                type="button"
                className="rounded-lg border border-ops-border px-3 py-1.5 text-sm"
                disabled={!manualCapabilitiesJson.trim() || manualCapsMutation.isPending}
                onClick={() => manualCapsMutation.mutate()}
              >
                Сохранить manual snapshot
              </button>
            </section>
          ) : null}

          {v2Data.provider_snapshot ? (
            <section className="ops-card space-y-3 p-4">
              <h2 className="text-base font-semibold">Provider snapshot</h2>
              <pre className="max-h-48 overflow-auto rounded-lg bg-ops-surface p-3 text-xs text-ops-muted">
                {JSON.stringify(v2Data.provider_snapshot, null, 2)}
              </pre>
            </section>
          ) : null}

          <section className="ops-card space-y-3 p-4">
            <h2 className="text-base font-semibold">Capability diff и publish gates</h2>
            <pre className="max-h-64 overflow-auto rounded-lg bg-ops-surface p-3 text-xs text-ops-muted">
              {JSON.stringify({
                readiness: v2Data.readiness,
                sync: v2Data.capability_sync,
                diff: v2Data.capability_diff,
                accepted: v2Data.accepted_provider_snapshot,
              }, null, 2)}
            </pre>
          </section>

          <section className="ops-card space-y-3 p-4">
            <h2 className="text-base font-semibold">Capability history</h2>
            <pre className="max-h-64 overflow-auto rounded-lg bg-ops-surface p-3 text-xs text-ops-muted">
              {JSON.stringify(capabilityHistory ?? [], null, 2)}
            </pre>
          </section>

          <section className="ops-card space-y-3 p-4">
            <h2 className="text-base font-semibold">Config history</h2>
            <div className="space-y-2">
              {v2Data.config_history.map((item) => (
                <div key={item.version} className="flex items-center justify-between text-sm">
                  <span>v{item.version} · {item.status}</span>
                  {canEdit && item.status === 'archived' ? (
                    <button
                      type="button"
                      className="rounded-lg border border-ops-border px-3 py-1.5"
                      disabled={rollbackV2Mutation.isPending}
                      onClick={() => rollbackV2Mutation.mutate(item.version)}
                    >
                      Rollback
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          {canEdit ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg border border-ops-border px-3 py-1.5 text-sm"
                disabled={saveDraftMutation.isPending}
                onClick={() => saveDraftMutation.mutate(draft)}
              >
                Сохранить draft
              </button>
              <button
                type="button"
                className="rounded-lg border border-ops-border px-3 py-1.5 text-sm"
                disabled={validateV2Mutation.isPending}
                onClick={() => validateV2Mutation.mutate()}
              >
                Validate
              </button>
              <button
                type="button"
                className="rounded-lg border border-ops-border px-3 py-1.5 text-sm"
                disabled={syncCapsMutation.isPending}
                onClick={() => syncCapsMutation.mutate()}
              >
                Sync capabilities
              </button>
              <button
                type="button"
                className="rounded-lg border border-ops-border px-3 py-1.5 text-sm"
                disabled={!v2Data.draft || importCapsMutation.isPending}
                onClick={() => importCapsMutation.mutate()}
              >
                Import capabilities в draft
              </button>
              <button
                type="button"
                className="rounded-lg bg-ops-accent px-3 py-1.5 text-sm text-white disabled:opacity-50"
                disabled={!v2Data.draft || publishV2Mutation.isPending}
                onClick={() => publishV2Mutation.mutate()}
              >
                Publish v2
              </button>
            </div>
          ) : (
            <p className="text-sm text-ops-muted">{copy.models.readOnly}</p>
          )}
        </div>
      </div>
    </div>
  );
}
